import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import {
  AppIcon,
  AppIconNames,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import { Dialog, DialogTitle } from 'src/components/molecules/dialog'

import { ICompanyDetailModalProps, ICompanyLinks } from '../types'
import { FeaturedVideo } from './featured-video'

interface ISocialLink {
  key: keyof ICompanyLinks
  icon: AppIconNames
  label: string
}

const secondaryButtonClassName =
  'flex h-8 w-8 items-center justify-center rounded-full border border-border-default text-content-primary transition-colors hover:bg-state-hover-surface'

export const CompanyDetailModal = ({
  company,
  isOpen,
  onClose,
}: ICompanyDetailModalProps): JSX.Element => {
  const { t } = useTranslation()

  const socialLinks: ISocialLink[] = useMemo(
    () => [
      {
        key: 'x',
        icon: AppIconNames.xLogo,
        label: t('whyWeInvest.modal.xAriaLabel'),
      },
      {
        key: 'linkedin',
        icon: AppIconNames.linkedinLogo,
        label: t('whyWeInvest.modal.linkedinAriaLabel'),
      },
      {
        key: 'website',
        icon: AppIconNames.globe,
        label: t('whyWeInvest.modal.websiteAriaLabel'),
      },
    ],
    [t],
  )

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      dismissable
      overlayClassName="!bg-black/50 z-[54]"
      contentClassName="flex flex-col z-[55] w-[calc(100vw-2rem)] md:w-[min(1074px,calc(100vw-8rem))] h-[calc(100dvh-2rem)] md:h-[calc(100dvh-128px)] rounded-[20px] overflow-hidden bg-surface-page"
    >
      {company && (
        <>
          <DialogTitle asChild>
            <header className="flex flex-col gap-4 px-6 pt-6 md:px-8 md:pt-8">
              <div className="flex items-start justify-between gap-4 md:items-center">
                <div className="flex min-w-0 flex-col gap-2">
                  <img
                    src={company.logo}
                    alt={company.name}
                    className="h-8 w-auto max-w-[200px] object-contain"
                  />

                  <div className="flex items-center gap-2 md:hidden">
                    {socialLinks.map(({ key, icon, label }) => {
                      const href = company.links[key]
                      if (!href) {
                        return null
                      }
                      return (
                        <a
                          key={key}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          className={secondaryButtonClassName}
                        >
                          <AppIcon name={icon} size={16} />
                        </a>
                      )
                    })}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <div className="hidden items-center gap-2 md:flex">
                    {socialLinks.map(({ key, icon, label }) => {
                      const href = company.links[key]
                      if (!href) {
                        return null
                      }
                      return (
                        <a
                          key={key}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          className={secondaryButtonClassName}
                        >
                          <AppIcon name={icon} size={16} />
                        </a>
                      )
                    })}
                  </div>

                  <span
                    aria-hidden
                    className="hidden h-8 w-px bg-border md:block"
                  />

                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('whyWeInvest.modal.closeAriaLabel')}
                    className={secondaryButtonClassName}
                  >
                    <AppIcon name={AppIconNames.close} size={16} />
                  </button>
                </div>
              </div>

              <span aria-hidden className="h-px w-full bg-border" />
            </header>
          </DialogTitle>

          <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-5 md:gap-8 md:px-8 md:pb-8 md:pt-6">
            <Typography
              variant={TypographyVariant.titleSm}
              weight={TypographyWeight.medium}
              className="text-justify leading-[1.5] text-content-primary"
            >
              {company.description}
            </Typography>

            {company.featuredVideo && (
              <section className="flex flex-col gap-2">
                <Typography
                  variant={TypographyVariant.bodyXxsStrong}
                  className="uppercase leading-[1.55] text-content-secondary"
                >
                  {t('whyWeInvest.modal.featuredVideo')}
                </Typography>
                <FeaturedVideo
                  url={company.featuredVideo}
                  title={company.name}
                />
              </section>
            )}

            <section className="flex flex-col gap-2">
              <Typography
                variant={TypographyVariant.bodyXxsStrong}
                className="uppercase leading-[1.55] text-content-secondary"
              >
                {t('whyWeInvest.modal.investmentThesis')}
              </Typography>
              <div className="flex flex-col gap-4">
                {company.investmentThesis.map((paragraph, index) => (
                  <Typography
                    key={index}
                    variant={TypographyVariant.bodyXs}
                    className="text-justify leading-[1.45] text-content-primary"
                  >
                    {paragraph}
                  </Typography>
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </Dialog>
  )
}
